/**
 * Utilitários de formatação para o módulo de atendimento WhatsApp.
 * Funções para formatar horários de mensagens, truncar previews,
 * e formatar números de telefone E.164.
 */
import { aawzDate } from '@utils/dates/aawz-date'
import { parseDateSafe } from '@utils/Format'

/**
 * Nomes dos dias da semana em PT-BR, indexados por `Date.prototype.getDay()`
 * (0 = Domingo ... 6 = Sábado). Usado por `formatConversaListTime` para
 * rotular conversas dentro da mesma semana, como no WhatsApp.
 */
export const WEEKDAYS_PT = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
]

// Janela (em dias) em que o WhatsApp mostra o nome do dia da semana em vez de
// "Ontem" ou da data completa. 2 a 6 dias atrás → dia da semana; 7+ → data.
const WEEKDAY_LABEL_MIN_DIFF = 2
const WEEKDAY_LABEL_MAX_DIFF = 6

// Datas/horas via aawzDate. O `format` do aawzDate usa componentes UTC para a
// DATA (getUTCDate/Month/Year); por isso normalizamos para a meia-noite LOCAL
// com `.startOf('day')` antes de formatar a data — assim o dia exibido é o
// local correto (fuso de produção, UTC-3). O horário usa `format('HH:mm')`
// direto (o token HH já é getHours/local).

/**
 * Diferença em dias-calendário entre hoje e a data informada (positivo no
 * passado). Compara a meia-noite LOCAL de cada data via aawzDate.
 * @param {Date} date
 * @returns {number}
 */
function diffCalendarDaysFromToday(date) {
  const today = aawzDate(new Date()).startOf('day')
  return today.diff(aawzDate(date).startOf('day').toDate(), 'days')
}

/**
 * Parseia data do backend que pode vir como "DD/MM/YYYY HH:mm",
 * "DD/MM/YYYY" ou ISO 8601. Delega para o helper canônico
 * `parseDateSafe` (em `@utils/Format`).
 *
 * @param {string|null|undefined} str
 * @returns {Date|null}
 */
export function parseBackendDate(str) {
  if (!str) return null
  const parsed = parseDateSafe({ date: str, fallback: null })
  return parsed ? parsed.toDate() : null
}

/**
 * Formata o timestamp de uma mensagem como HH:mm (hora local). O contexto de
 * data fica a cargo do separador de datas (`formatDateSeparator`).
 *
 * @param {string|null|undefined} dateString - Data ISO 8601 ou DD/MM/YYYY HH:mm
 * @returns {string} Horário HH:mm ou string vazia se inválida
 */
export function formatMessageTime(dateString) {
  if (!dateString) return ''

  const date = parseBackendDate(dateString)
  if (!date || Number.isNaN(date.getTime())) return ''

  return aawzDate(date).format('HH:mm')
}

/**
 * Returns a date label for the separator between message groups.
 * Matches WhatsApp Web behavior:
 *   - "Hoje" for today
 *   - "Ontem" for yesterday
 *   - "DD/MM/YYYY" for older dates
 *
 * @param {string} dateString - Date from backend (DD/MM/YYYY HH:mm or ISO)
 * @returns {string} Human-readable date label
 */
export function formatDateSeparator(dateString) {
  if (!dateString) return ''

  const date = parseBackendDate(dateString)
  if (!date || Number.isNaN(date.getTime())) return ''

  const diffDays = diffCalendarDaysFromToday(date)

  if (diffDays === 0) return 'Hoje'
  if (diffDays === 1) return 'Ontem'

  return aawzDate(date).startOf('day').format('DD/MM/YYYY')
}

/**
 * Formata o horário exibido na lista lateral de conversas, espelhando o
 * comportamento do WhatsApp (diferente das bolhas de mensagem, que sempre
 * mostram HH:mm via `formatMessageTime`):
 *   - Hoje                       → HH:mm
 *   - Ontem                      → "Ontem"
 *   - Entre 2 e 6 dias atrás     → nome do dia da semana em PT-BR
 *   - 7 dias ou mais (ou futuro) → DD/MM/AAAA
 *
 * Reusa `parseBackendDate` (mesmo parser do separador de datas) e a lógica de
 * `diffDays` de `formatDateSeparator`.
 *
 * @param {string|null|undefined} dateString - Data do backend (DD/MM/YYYY HH:mm ou ISO)
 * @returns {string} Rótulo de horário/data ou string vazia se inválida
 */
export function formatConversaListTime(dateString) {
  if (!dateString) return ''

  const date = parseBackendDate(dateString)
  if (!date || Number.isNaN(date.getTime())) return ''

  const diffDays = diffCalendarDaysFromToday(date)

  if (diffDays === 0) return aawzDate(date).format('HH:mm')
  if (diffDays === 1) return 'Ontem'

  if (diffDays >= WEEKDAY_LABEL_MIN_DIFF && diffDays <= WEEKDAY_LABEL_MAX_DIFF) {
    return WEEKDAYS_PT[date.getDay()]
  }

  return aawzDate(date).startOf('day').format('DD/MM/YYYY')
}

/**
 * Trunca texto para preview de mensagem.
 *
 * @param {string|null|undefined} text - Texto a truncar
 * @param {number} maxLength - Comprimento máximo (padrão 100)
 * @returns {string} Texto truncado com "..." se exceder o limite
 */
export function truncatePreview(text, maxLength = 100) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/**
 * Formata número de telefone E.164 para exibição.
 * - Números brasileiros (+55): +55 (XX) XXXXX-XXXX
 * - Outros: retorna como recebido
 *
 * @param {string|null|undefined} phone - Número em formato E.164
 * @returns {string} Número formatado ou string vazia
 */
export function formatPhone(phone) {
  if (!phone) return ''

  const digits = phone.replaceAll(/\D/g, '')

  if (digits.length === 13 && digits.startsWith('55')) {
    const ddd = digits.substring(2, 4)
    const part1 = digits.substring(4, 9)
    const part2 = digits.substring(9, 13)
    return `+55 (${ddd}) ${part1}-${part2}`
  }

  if (digits.length === 12 && digits.startsWith('55')) {
    const ddd = digits.substring(2, 4)
    const part1 = digits.substring(4, 8)
    const part2 = digits.substring(8, 12)
    return `+55 (${ddd}) ${part1}-${part2}`
  }

  return phone.startsWith('+') ? phone : `+${phone}`
}

/**
 * Sanitiza o conteúdo de uma mensagem para renderização segura como HTML.
 * - Faz escape das entidades HTML básicas (&, <, >, ", ').
 * - Aplica formatação markdown estilo WhatsApp:
 *     `*texto*`     → <strong>
 *     `_texto_`     → <em>
 *     `~texto~`     → <s>
 *     ``` `texto` ```    → <code> inline
 *     ``` ```texto``` ``` → <code> bloco (multiline)
 * - Converte URLs http(s) em links clicáveis com `rel="noopener noreferrer"`.
 * - Preserva quebras de linha (\n → <br>).
 *
 * Ordem: escape HTML → markdown → linkify → \n→<br>. Markdown roda antes do
 * linkify pra não corromper URLs já envolvidas em <a>; HTML escape roda antes
 * de tudo pra neutralizar <script> e on* injetados.
 *
 * @param {string|null|undefined} text - Conteúdo bruto da mensagem
 * @returns {string} HTML pronto para uso com `v-html`
 */
export function formatMessageContent(text) {
  if (!text) return ''
  let html = text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
  // Code block (triplo) vem antes do inline pra não ser "comido" pelo `...`.
  html = html.replaceAll(
    /```([\s\S]+?)```/g,
    '<code class="message-code-block">$1</code>'
  )
  html = html.replaceAll(
    /`([^`\n]+)`/g,
    '<code class="message-code-inline">$1</code>'
  )
  // Bold/italic/strike: conteúdo tem que começar e terminar com não-espaço
  // (evita matches acidentais tipo `a * b` no meio do texto).
  html = html.replaceAll(
    /\*([^\s*][^*\n]*?[^\s*]|[^\s*])\*/g,
    '<strong>$1</strong>'
  )
  html = html.replaceAll(/_([^\s_][^_\n]*?[^\s_]|[^\s_])_/g, '<em>$1</em>')
  html = html.replaceAll(/~([^\s~][^~\n]*?[^\s~]|[^\s~])~/g, '<s>$1</s>')
  html = html.replaceAll(
    /(https?:\/\/[^\s<&"']+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="message-link">$1</a>'
  )
  html = html.replaceAll('\n', '<br>')
  return html
}
